import { useSearchParams } from 'next/navigation'
import { useMemo } from 'react'

import { type OfferV11 } from '@redteclab/api/clients/bully'

import { productAccessorIsProductFreebie } from '../../../../model/productAccessor/productAccessorIsProductFreebie'
import { productAccessorIsProductNotInCatalog } from '../../../../model/productAccessor/productAccessorIsProductNotInCatalog'
import { productAccessorIsProductOutOfSale } from '../../../../model/productAccessor/productAccessorIsProductOutOfSale'
import { productAccessorIsProductOutOfStock } from '../../../../model/productAccessor/productAccessorIsProductOutOfStock'
import {
  productAddToCartParamsMakeProductPageMainCtaParams,
  type ProductAddToCartProductPageMainCtaParams,
} from '../../../../product-add-to-cart-params/productAddToCartParams'
import { useProductPageContext } from '../../../productPageContext'

import { useIsPrivatePrescriptionAndCartMetadataHasErx } from './useIsPrivatePrescriptionAndCartMetadataHasErx'

export const useBuildAddToCartParams = ({
  offer,
}: {
  offer?: OfferV11
} = {}): {
  addToCartParams?: ProductAddToCartProductPageMainCtaParams
  disableCta: boolean
  isPrivatePrescriptionAndCartMetadataHasErx?: boolean
} => {
  const {
    attributeSelectionControls,
    channel,
    currentOffer,
    currentPrescriptionType,
    product,
    quantitySelectionControls,
    rxByPrincipleControls,
  } = useProductPageContext()

  const selectedOffer = offer ?? currentOffer

  const searchParams = useSearchParams()
  const queryId = searchParams.get('queryID') ?? ''
  const query = searchParams.get('query') ?? ''

  const isPrivatePrescriptionAndCartMetadataHasErx =
    useIsPrivatePrescriptionAndCartMetadataHasErx(currentPrescriptionType)

  return useMemo(() => {
    const offerId = selectedOffer?.offerId

    if (!offerId) {
      return {
        disableCta: true,
      }
    }

    const attributeNotSelectedError =
      attributeSelectionControls.attributeNotSelectedErrorControls
        .noAttributeSelectedError

    if (
      attributeNotSelectedError ||
      isPrivatePrescriptionAndCartMetadataHasErx ||
      productAccessorIsProductOutOfStock(product.stock_status) ||
      productAccessorIsProductOutOfSale(product.stock_status) ||
      productAccessorIsProductNotInCatalog(product.stock_status) ||
      productAccessorIsProductFreebie(product.stock_status) ||
      !attributeSelectionControls.variantMatchingSelection
    ) {
      return { disableCta: true, isPrivatePrescriptionAndCartMetadataHasErx }
    }

    const addToCartParams = productAddToCartParamsMakeProductPageMainCtaParams({
      channel,
      offerId,
      prescriptionType: currentPrescriptionType,
      productId: product.upid,
      quantity: quantitySelectionControls.quantity,
      query,
      queryId,
      rxByPrincipleControls,
    })

    return {
      addToCartParams,
      disableCta: false,
    }
  }, [
    attributeSelectionControls.attributeNotSelectedErrorControls
      .noAttributeSelectedError,
    attributeSelectionControls.variantMatchingSelection,
    channel,
    selectedOffer?.offerId,
    currentPrescriptionType,
    isPrivatePrescriptionAndCartMetadataHasErx,
    product.stock_status,
    product.upid,
    quantitySelectionControls.quantity,
    rxByPrincipleControls,
    query,
    queryId,
  ])
}
